import { css } from '@emotion/react';
import {
  faCrosshairs,
  faFlag, faLocationArrow,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useEffect,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { TextInputWithSuffixComponent } from '~/_shared/baseComponents/inputs';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { useTimeout } from '~/_shared/utils/hooks/useTimeout';
import { OverlayLoaderComponent } from '../overlay/overlayLoader.component';

const inputSuffixButtonStyle = (theme: Theme) => css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  background: 'none',
  border: 'none',
  color: 'inherit',
  transition: 'color .15s',
  '&:hover': {
    color: theme.textColors.link,
  },
});

const activeSuffixButtonStyle = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.quaternary,
  color: theme.iconColors.quaternary,
  '&:hover': {
    backgroundColor: theme.iconColors.backgroundQuaternaryHover,
    color: theme.iconColors.quaternary,
  },
});

const separatorRightStyle = (theme: Theme) => css({
  borderRight: `1px solid ${theme.borderColors.primary}`,
});

const inputSuffixIconStyle = css({
  fontSize: 20,
});

const textInputSuffixStyle = css({
  position: 'relative',
  height: '100%',
  display: 'flex',
});

export type TextInputWithGeolocationProps = Readonly<{
  hasError?: boolean;
  isLoading?: boolean;
  isSelectLocationActive?: boolean;
  placeholder?: string;
  value: string;

  onChange: (newValue: string) => void;
  onGetUserLocation: () => void;
  onSelectLocationOnMap: () => void;
}>;

export const TextInputWithGeolocationComponent: FC<TextInputWithGeolocationProps> = props => {
  const [t] = useTranslation();

  const [showErrorMessage, setShowErrorMessage] = useTimeout(3000);

  useEffect(() => {
    if (props.hasError) {
      setShowErrorMessage();
    }
  }, [props.hasError, setShowErrorMessage]);

  return (
    <TextInputWithSuffixComponent
      value={props.value}
      onChange={props.onChange}
      placeholder={props.placeholder}
      isReadOnly={props.isLoading}
      icon={faFlag}
      isInvalid={props.hasError}
      suffix={(
        <div css={textInputSuffixStyle}>
          <TooltipComponent
            delayShow={500}
            tooltipContent={t('Select Location on Map')}
            behavior={TooltipBehavior.ShowOnHover}
          >
            <button
              css={[inputSuffixButtonStyle, separatorRightStyle, ...(props.isSelectLocationActive ? [activeSuffixButtonStyle] : [])]}
              onClick={props.onSelectLocationOnMap}
            >
              <FontAwesomeIcon
                icon={faCrosshairs}
                css={inputSuffixIconStyle}
              />
            </button>
          </TooltipComponent>
          <TooltipComponent
            tooltipContent={showErrorMessage
              ? t('geolocationInput.errors.general')
              : t('My Location')
            }
            behavior={showErrorMessage ? TooltipBehavior.ShowAlways : TooltipBehavior.ShowOnHover}
            delayShow={showErrorMessage ? 0 : 500}
          >
            <button
              css={inputSuffixButtonStyle}
              onClick={props.onGetUserLocation}
            >
              <FontAwesomeIcon
                icon={faLocationArrow}
                css={inputSuffixIconStyle}
              />
            </button>
          </TooltipComponent>
          {props.isLoading && (
            <OverlayLoaderComponent
              loaderSize={30}
            />
          )}
        </div>
      )}
    />
  );
};
